import { Link, useLocation } from 'react-router-dom';
import { LuCalendar, LuCheck, LuClock } from 'react-icons/lu';
import Page from '../../Components/Page/BookingPage';
import { formatPrice } from '../../utils/pricing';
import { formatMinutesAsTime, parseTimeToMinutes } from '../../utils/time';
import { normalizeBookingServices } from '../../utils/serviceBuilder';
import styles from './paymentSuccess.module.css';

function PaymentSuccess() {
    const location = useLocation();
    const state = location?.state ?? {};
    const booking = state.booking ?? {};
    const services = normalizeBookingServices(booking);
    const appointmentDate = booking.date || state.date;
    const slotValue = booking.slotValue || state.start_time;
    const startTime = slotValue ? formatMinutesAsTime(parseTimeToMinutes(slotValue)) : '';
    const endTime = state.endTime || state.end_time || '';
    const totalPrice = Number(booking.total_price);
    const deposit = state.deposit;
    const hasDeposit = Number.isFinite(deposit) && deposit > 0;
    const formattedDate = appointmentDate
        ? new Date(`${appointmentDate}T00:00:00`).toLocaleDateString('en-US', {
            weekday: 'long',
            month: 'long',
            day: 'numeric',
            year: 'numeric',
        })
        : '';

    const steps = [
        'We review your request and confirm availability.',
        "You'll get a text or email once it's confirmed.",
        hasDeposit
            ? `Your ${formatPrice(deposit)} deposit is charged when your appointment is confirmed.`
            : deposit === 0
                ? 'No deposit is required. Your full balance is collected after your appointment.'
                : 'Any required deposit is charged at confirmation; the balance is collected after your appointment.',
    ];

    return (
        <Page
            title="Appointment requested"
            subtitle="Thank you! Your request is in for review."
            grid={false}
            centered
        >
            <section className={styles.card}>
                <div className={styles.iconWrap} aria-hidden="true">
                    <LuCheck />
                </div>

                {(services.length > 0 || formattedDate) && (
                    <div className={styles.summary}>
                        <h2 className={styles.sectionTitle}>Your appointment</h2>
                        {services.length > 0 && (
                            <ul className={styles.services}>
                                {services.map((service) => (
                                    <li key={service.product_id} className={styles.serviceRow}>
                                        <span>{service.product_name}</span>
                                        {Number.isFinite(Number(service.total_price)) && (
                                            <span className={styles.price}>{formatPrice(service.total_price)}</span>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        )}
                        {formattedDate && (
                            <div className={styles.detailRow}>
                                <LuCalendar aria-hidden="true" className={styles.detailIcon} />
                                <span>{formattedDate}</span>
                            </div>
                        )}
                        {startTime && (
                            <div className={styles.detailRow}>
                                <LuClock aria-hidden="true" className={styles.detailIcon} />
                                <span>{startTime}{endTime ? ` – ${endTime}` : ''}</span>
                            </div>
                        )}
                        {Number.isFinite(totalPrice) && totalPrice > 0 && (
                            <div className={styles.totalRow}>
                                <span>Appointment total</span>
                                <span className={styles.price}>{formatPrice(totalPrice)}</span>
                            </div>
                        )}
                    </div>
                )}

                <div className={styles.next}>
                    <h2 className={styles.sectionTitle}>What happens next</h2>
                    <ol className={styles.steps}>
                        {steps.map((step) => (
                            <li key={step}>{step}</li>
                        ))}
                    </ol>
                </div>

                <div className={styles.actions}>
                    <Link to="/home" className={styles.primary}>Back to home</Link>
                    <Link to="/book" className={styles.secondary}>Book another appointment</Link>
                </div>
            </section>
        </Page>
    );
}

export default PaymentSuccess;